import { Activity, useEffect, useEffectEvent, useRef, useState } from 'react'

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { useTheme } from '@mui/material'

const SearchSelect = ({
  options,
  label,
  onSelectionChange,
  selectedValue,
  width = '250px',
  size = 'small',
  color,
}) => {
  const theme = useTheme()

  const [open, setOpen] = useState(false)
  const inputRef = useRef(null)

  options.map((option) => (option.id = option.uuid))

  const selectDefault = useEffectEvent(() => onSelectionChange(options[0]))

  useEffect(() => {
    if (options.length >= 1 && selectedValue === '') {
      selectDefault()
    }
  }, [options, selectedValue])

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus()
    }
  }, [open])

  const handleSelect = (event, value) => {
    onSelectionChange(value)
    setOpen(false)
  }

  const selectedDevice =
    options.find((device) => device.uuid === selectedValue?.uuid) || {}

  return (
    <div>
      <Activity mode={!open ? 'visible' : 'hidden'}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            width,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            padding: 0,
            font: 'inherit',
            color: 'inherit',
            textAlign: 'left',
          }}
        >
          <span>
            {label}: {selectedDevice.name}
          </span>
          {options.length > 1 && <ArrowDropDownIcon />}
        </button>
      </Activity>
      <Activity mode={open ? 'visible' : 'hidden'}>
        <Autocomplete
          id={label.replace(/\s+/g, '-').toLowerCase()}
          size={size}
          sx={{ width }}
          options={options}
          open={open}
          disableClearable
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={selectedDevice}
          getOptionLabel={(option) => option?.name ?? ''}
          filterOptions={(options, { inputValue }) => {
            const searchTerm = inputValue.toLowerCase()
            return options.filter(
              (option) =>
                option.name.toLowerCase().includes(searchTerm) ||
                (option.ip && option.ip.toLowerCase().includes(searchTerm)),
            )
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              margin="none"
              inputRef={inputRef}
              placeholder="Search by name or IP"
              color={color ?? 'primary'}
              sx={
                color && {
                  input: {
                    color: theme.palette[color].main,
                  },
                  button: {
                    color: theme.palette[color].main,
                  },
                }
              }
            />
          )}
          renderOption={({ key, ...props }, option, { inputValue }) => {
            // Match for name
            const nameMatches = match(option.name, inputValue, {
              insideWords: true,
            })
            const nameParts = parse(option.name, nameMatches)

            // Match for IP
            const ipMatches = option.ip
              ? match(option.ip, inputValue, {
                  insideWords: true,
                })
              : []
            const ipParts = option.ip ? parse(option.ip, ipMatches) : []

            return (
              <li key={key} {...props}>
                <div>
                  {/* Render highlighted name */}
                  {nameParts.map((part, index) => (
                    <span
                      key={`name-${index}`}
                      style={{ fontWeight: part.highlight ? 700 : 400 }}
                    >
                      {part.text}
                    </span>
                  ))}
                  <br />
                  {/* Render highlighted IP */}
                  <small>
                    {ipParts.map((part, index) => (
                      <span
                        key={`ip-${index}`}
                        style={{
                          fontWeight: part.highlight ? 700 : 400,
                        }}
                      >
                        {part.text}
                      </span>
                    ))}
                  </small>
                </div>
              </li>
            )
          }}
          onChange={handleSelect}
        />
      </Activity>
    </div>
  )
}

export default SearchSelect
